import { useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  BookOpenText,
  CheckCircle2,
  Flame,
  PenLine,
  Target,
  Zap,
} from "lucide-react";
import type { ComponentType, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { CefrLevel } from "@engo/shared";
import { GlassCard } from "../../components/ui/GlassCard.js";
import { api } from "../../lib/api.js";

const CEFR_COLORS: Record<CefrLevel, string> = {
  A1: "bg-emerald-400",
  A2: "bg-teal-400",
  B1: "bg-sky-400",
  B2: "bg-indigo-400",
  C1: "bg-violet-400",
  C2: "bg-rose-400",
};

const TOP_TOPICS = 6;

export function DashboardPage() {
  const { t } = useTranslation();
  const { data: user } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: () => api.me(),
    staleTime: 60_000,
  });
  const statsQuery = useQuery({
    queryKey: ["stats", "dashboard"],
    queryFn: () => api.getDashboardStats(),
    staleTime: 30_000,
  });
  const stats = statsQuery.data;

  const maxDayCount = stats ? Math.max(1, ...stats.reviews_by_day.map((d) => d.count)) : 1;
  const topTopics = stats?.topic_distribution.slice(0, TOP_TOPICS) ?? [];
  const maxTopicCount = Math.max(1, ...topTopics.map((x) => x.count));
  const cefrTotal = stats?.cefr_distribution.reduce((sum, x) => sum + x.count, 0) ?? 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-[var(--text-primary)]">
          {t("dashboard.title")}
        </h1>
        <p className="mt-1 text-[var(--text-secondary)]">
          {user?.name
            ? t("dashboard.welcomeWithName", { name: user.name })
            : t("dashboard.welcomeNoName")}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Link to="/review" className="block">
          <StatCard
            icon={Zap}
            label={t("dashboard.dueNow")}
            value={stats ? String(stats.due_now) : "—"}
            caption={t("dashboard.dueNowCaption")}
            accent
            interactive
          />
        </Link>
        <StatCard
          icon={Flame}
          label={t("dashboard.streak")}
          value={stats ? String(stats.streak_days) : "—"}
          caption={t("dashboard.streakCaption", { count: stats?.streak_days ?? 0 })}
        />
        <StatCard
          icon={CheckCircle2}
          label={t("dashboard.reviewedToday")}
          value={stats ? String(stats.reviews_today) : "—"}
          caption={t("dashboard.reviewedTodayCaption")}
        />
        <StatCard
          icon={Target}
          label={t("dashboard.retention")}
          value={
            stats
              ? stats.retention_30d === null
                ? "—"
                : `${Math.round(stats.retention_30d * 100)}%`
              : "—"
          }
          caption={t("dashboard.retentionCaption")}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <GlassCard>
          <SectionTitle icon={BookOpen}>
            {t("dashboard.vocabTitle", { count: stats?.entries_total ?? 0 })}
          </SectionTitle>
          {stats && cefrTotal > 0 ? (
            <div className="mt-4 space-y-3">
              <div className="flex h-3 w-full overflow-hidden rounded-full bg-white/5">
                {stats.cefr_distribution.map((item) => (
                  <div
                    key={item.level}
                    className={CEFR_COLORS[item.level]}
                    style={{ width: `${(item.count / cefrTotal) * 100}%` }}
                    title={`${item.level}: ${item.count}`}
                  />
                ))}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                {stats.cefr_distribution.map((item) => (
                  <span
                    key={item.level}
                    className="inline-flex items-center gap-1.5 text-xs text-[var(--text-secondary)]"
                  >
                    <span className={`h-2 w-2 rounded-full ${CEFR_COLORS[item.level]}`} aria-hidden />
                    {item.level}
                    <span className="text-[var(--text-muted)]">{item.count}</span>
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <EmptyHint>{t("dashboard.noVocabData")}</EmptyHint>
          )}

          {topTopics.length > 0 ? (
            <div className="mt-5 space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-[var(--text-muted)]">
                {t("dashboard.topTopics")}
              </p>
              {topTopics.map((item) => (
                <div key={item.topic} className="flex items-center gap-2">
                  <span className="w-36 shrink-0 truncate text-xs text-[var(--text-secondary)]">
                    {t(`topic.${item.topic}`)}
                  </span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/5">
                    <div
                      className="h-full rounded-full bg-[var(--primary)]/70"
                      style={{ width: `${(item.count / maxTopicCount) * 100}%` }}
                    />
                  </div>
                  <span className="w-6 shrink-0 text-right font-mono text-xs text-[var(--text-muted)]">
                    {item.count}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </GlassCard>

        <GlassCard>
          <SectionTitle icon={Zap}>{t("dashboard.activityTitle")}</SectionTitle>
          {stats ? (
            <>
              <div className="mt-4 flex items-end gap-1">
                {stats.reviews_by_day.map((day) => (
                  <div
                    key={day.date}
                    className="flex flex-1 flex-col items-center gap-1"
                    title={`${day.date}: ${day.count}`}
                  >
                    <div
                      className={`w-full rounded-sm ${day.count > 0 ? "bg-[var(--primary)]/70" : "bg-white/5"}`}
                      style={{ height: `${Math.max(4, (day.count / maxDayCount) * 96)}px` }}
                    />
                    <span className="text-[9px] text-[var(--text-muted)]">
                      {Number(day.date.slice(8, 10))}
                    </span>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-[var(--text-muted)]">
                {t("dashboard.activityCaption", {
                  count: stats.reviews_by_day.reduce((sum, d) => sum + d.count, 0),
                })}
              </p>
            </>
          ) : (
            <EmptyHint>{t("common.loading")}</EmptyHint>
          )}
        </GlassCard>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <QuickLink
          to="/review"
          icon={Zap}
          title={t("dashboard.quickReview")}
          caption={t("dashboard.quickReviewCaption", { count: stats?.due_now ?? 0 })}
        />
        <QuickLink
          to="/practice/reading"
          icon={BookOpenText}
          title={t("dashboard.quickReading")}
          caption={t("dashboard.quickReadingCaption")}
        />
        <QuickLink
          to="/practice/writing"
          icon={PenLine}
          title={t("dashboard.quickWriting")}
          caption={t("dashboard.quickWritingCaption")}
        />
      </div>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  caption,
  accent = false,
  interactive = false,
}: {
  icon: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  label: string;
  value: string;
  caption: string;
  accent?: boolean;
  interactive?: boolean;
}) {
  return (
    <GlassCard
      className={
        interactive
          ? "h-full transition-[transform,box-shadow,background-color] hover:-translate-y-0.5 hover:bg-[var(--glass-bg-strong)] hover:shadow-[var(--glow-accent)]"
          : "h-full"
      }
    >
      <div className="flex items-center gap-3">
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
            accent ? "bg-[var(--primary)]/12 text-[var(--accent)]" : "bg-white/5 text-[var(--text-muted)]"
          }`}
        >
          <Icon className="h-4 w-4" aria-hidden />
        </span>
        <p className="text-xs font-medium uppercase tracking-wide text-[var(--text-muted)]">{label}</p>
      </div>
      <p
        className={`mt-3 font-mono text-3xl font-semibold ${
          accent ? "text-[var(--accent)]" : "text-[var(--text-primary)]"
        }`}
      >
        {value}
      </p>
      <p className="mt-1 text-xs text-[var(--text-muted)]">{caption}</p>
    </GlassCard>
  );
}

function SectionTitle({
  icon: Icon,
  children,
}: {
  icon: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-[var(--text-muted)]" aria-hidden />
      <p className="text-sm font-medium text-[var(--text-primary)]">{children}</p>
    </div>
  );
}

function EmptyHint({ children }: { children: ReactNode }) {
  return <p className="mt-4 text-sm text-[var(--text-muted)]">{children}</p>;
}

function QuickLink({
  to,
  icon: Icon,
  title,
  caption,
}: {
  to: string;
  icon: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  title: string;
  caption: string;
}) {
  return (
    <Link to={to} className="block">
      <GlassCard className="h-full transition-[transform,box-shadow,background-color] hover:-translate-y-0.5 hover:bg-[var(--glass-bg-strong)] hover:shadow-[var(--glow-accent)]">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[var(--primary)]/12 text-[var(--accent)]">
            <Icon className="h-4 w-4" aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-[var(--text-primary)]">{title}</p>
            <p className="truncate text-xs text-[var(--text-muted)]">{caption}</p>
          </div>
        </div>
      </GlassCard>
    </Link>
  );
}
